import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ResourceTable from "@/components/risorse/ResourceTable";
import InterniTab from "@/components/risorse/InterniTab";
import ScadenzeTab from "@/components/risorse/ScadenzeTab";
import { SCHEMAS } from "@/components/risorse/schemas";
import ImportA3Dialog from "@/components/programma/ImportA3Dialog";
import { useAiStatus } from "@/lib/useAiStatus";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";

const TABS = [
  { v: "interni", l: "Dipendenti" },
  { v: "esterni", l: "Esterni" },
  { v: "mezzi", l: "Mezzi trasporto" },
  { v: "mezzi-cantiere", l: "Mezzi cantiere" },
  { v: "attrezzature", l: "Attrezzature" },
  { v: "sedi", l: "Sedi / Cantieri" },
  { v: "scadenze", l: "Scadenze" },
];

// Iter 105: "Parco risorse" — anagrafiche per il futuro programma giornaliero.
export default function RisorsePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || !!user?.can_edit_risorse;
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.v === params.get("tab")) ? params.get("tab") : "interni";
  const [sedi, setSedi] = useState([]);
  const [scad, setScad] = useState({ count: 0, scaduti: 0 });
  const [importOpen, setImportOpen] = useState(false);
  const ai = useAiStatus();

  const refresh = useCallback(() => {
    api.get("/risorse/sedi").then(({ data }) => setSedi(data || [])).catch(() => {});
    api.get("/risorse-scadenze", { params: { giorni: 30, count_only: true } }).then(({ data }) => setScad(data || {})).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const setTab = (v) => setParams({ tab: v });

  return (
    <div className="space-y-5" data-testid="risorse-page">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[240px]">
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Parco risorse</h1>
          <p className="text-sm text-slate-500 mt-1">Personale interno ed esterno, mezzi, attrezzature e sedi: la base dati unica per il programma giornaliero.</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setImportOpen(true)} className="bg-[#0044FF] hover:bg-[#0033CC]" data-testid="risorse-import-a3-btn">
            <Sparkles className="h-4 w-4 mr-1.5" /> Importa Word A3
          </Button>
        )}
      </div>
      <ImportA3Dialog open={importOpen} onClose={() => { setImportOpen(false); refresh(); }} ai={ai} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1">
          {TABS.map((t) => (
            <TabsTrigger key={t.v} value={t.v} className="relative data-[state=active]:bg-white data-[state=active]:text-[#0044FF] font-semibold" data-testid={`risorse-tab-btn-${t.v}`}>
              {t.l}
              {t.v === "scadenze" && scad.count > 0 && (
                <span className={`ml-1.5 inline-flex min-w-[18px] h-[18px] px-1 items-center justify-center rounded-full text-[10px] font-black text-white ${scad.scaduti > 0 ? "bg-red-600" : "bg-amber-500"}`} data-testid="risorse-scadenze-badge">{scad.count}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="interni" className="mt-4"><InterniTab isAdmin={isAdmin} sedi={sedi} onChanged={refresh} /></TabsContent>
        {["esterni", "mezzi", "mezzi-cantiere", "attrezzature", "sedi"].map((k) => (
          <TabsContent key={k} value={k} className="mt-4">
            {tab === k && <ResourceTable schema={SCHEMAS[k]} isAdmin={isAdmin} sedi={sedi} onChanged={refresh} />}
          </TabsContent>
        ))}
        <TabsContent value="scadenze" className="mt-4">{tab === "scadenze" && <ScadenzeTab onOpenKind={(k) => setTab(k === "interni" ? "interni" : k)} />}</TabsContent>
      </Tabs>
    </div>
  );
}
